/**
 * Offline Request Queue
 * Queues failed API requests when offline and retries them when back online
 */

class OfflineRequestQueue {
  constructor() {
    this.queue = this.loadQueue();
    this.isProcessing = false;
    this.listeners = [];
    
    // Listen for online event
    window.addEventListener('online', () => {
      this.processQueue();
    });
  }

  /**
   * Load queue from localStorage
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem('worldexplore-request-queue');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load request queue:', error);
      return [];
    }
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem('worldexplore-request-queue', JSON.stringify(this.queue));
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to save request queue:', error);
    }
  }

  /**
   * Add a failed request to the queue
   */
  addRequest(request) {
    // Create a unique ID for the request
    const queuedRequest = {
      id: Date.now() + Math.random(),
      timestamp: Date.now(),
      url: request.url,
      method: request.method || 'GET',
      headers: request.headers || {},
      body: request.body,
      retryCount: 0,
      maxRetries: 3
    };

    this.queue.push(queuedRequest);
    this.saveQueue();
    
    console.log('Request queued for retry:', queuedRequest.url);
    return queuedRequest.id;
  }

  /**
   * Remove a request from the queue
   */
  removeRequest(id) {
    this.queue = this.queue.filter(req => req.id !== id);
    this.saveQueue();
  }

  /**
   * Get all queued requests
   */
  getQueue() {
    return [...this.queue];
  }

  /**
   * Get queue size
   */
  getQueueSize() {
    return this.queue.length;
  }

  /**
   * Clear the entire queue
   */
  clearQueue() {
    this.queue = [];
    this.saveQueue();
  }

  /**
   * Process all queued requests
   */
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    console.log(`Processing ${this.queue.length} queued requests...`);

    // Create a copy to iterate over
    const queueCopy = [...this.queue];

    for (const request of queueCopy) {
      try {
        // Check if still online
        if (!navigator.onLine) {
          console.log('Went offline during queue processing');
          break;
        }

        await this.retryRequest(request);
        this.removeRequest(request.id);
        
      } catch (error) {
        console.error('Failed to retry request:', error);
        
        // Increment retry count
        request.retryCount++;
        
        // Remove if max retries exceeded
        if (request.retryCount >= request.maxRetries) {
          console.log('Max retries exceeded, removing request:', request.url);
          this.removeRequest(request.id);
        } else {
          this.saveQueue();
        }
      }
    }

    this.isProcessing = false;
    console.log('Queue processing complete');
    
    // Process again if more items were added
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 1000);
    }
  }

  /**
   * Retry a single request
   */
  async retryRequest(request) {
    const options = {
      method: request.method,
      headers: request.headers
    };

    if (request.body) {
      options.body = request.body;
    }

    const response = await fetch(request.url, options);
    
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response;
  }

  /**
   * Add listener for queue changes
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(cb => cb !== callback);
  }

  /**
   * Notify all listeners of queue changes
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.getQueueSize());
      } catch (error) {
        console.error('Error notifying queue listener:', error);
      }
    });
  }
}

// Create singleton instance
const offlineRequestQueue = new OfflineRequestQueue();

export default offlineRequestQueue;
